import { apiClient, apiFetch, bearer, unwrap } from './client';
import type { components } from './types';

export type InvoiceDocumentOut = components['schemas']['InvoiceDocumentOut'];
export type InvoicePoolOut = components['schemas']['InvoicePoolOut'];
export type InvoiceLinkOut = components['schemas']['InvoiceLinkOut'];
export type InvoiceLinkCreateIn = components['schemas']['InvoiceLinkCreateIn'];
export type InvoiceLinkFactsIn = components['schemas']['InvoiceLinkFactsIn'];
export type TransactionSearchOut = components['schemas']['TransactionSearchOut'];
export type TransactionSearchItemOut = components['schemas']['TransactionSearchItemOut'];
export type TransactionDocumentsOut = components['schemas']['TransactionDocumentsOut'];
export type NoInvoiceMarkOut = components['schemas']['NoInvoiceMarkOut'];
export type InvoiceDownloadOut = components['schemas']['InvoiceDownloadOut'];
export type InvoiceExtractionIn = components['schemas']['InvoiceExtractionIn'];
export type InvoiceExtractionOut = components['schemas']['InvoiceExtractionOut'];
export type DocumentationVerdictOut = components['schemas']['DocumentationVerdictOut'];
export type MatchSuggestionOut = components['schemas']['MatchSuggestionOut'];
export type MatchSuggestionsOut = components['schemas']['MatchSuggestionsOut'];
export type AutoMatchOut = components['schemas']['AutoMatchOut'];
export type AutoMatchedLinkOut = components['schemas']['AutoMatchedLinkOut'];
export type AutoMatchedLinksOut = components['schemas']['AutoMatchedLinksOut'];

/** Multipart upload — legacy `apiFetch` path per the repo convention.
 *  The drag-drop-many UI calls this once per file. */
export async function uploadInvoice(
  slug: string,
  file: File,
  idToken: string,
): Promise<InvoiceDocumentOut> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<InvoiceDocumentOut>(
    `/api/customers/${encodeURIComponent(slug)}/invoices`,
    { idToken, method: 'POST', body: form },
  );
}

export function listInvoicePool(
  slug: string,
  idToken: string,
  opts: {
    unassigned_only?: boolean;
    labelled?: boolean;
    filename?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<InvoicePoolOut> {
  return apiClient
    .GET('/api/customers/{slug}/invoices', {
      params: { path: { slug }, query: opts },
      headers: bearer(idToken),
    })
    .then(unwrap<InvoicePoolOut>);
}

export function getInvoiceDownload(
  slug: string,
  documentId: string,
  idToken: string,
): Promise<InvoiceDownloadOut> {
  return apiClient
    .GET('/api/customers/{slug}/invoices/{document_id}/download', {
      params: { path: { slug, document_id: documentId } },
      headers: bearer(idToken),
    })
    .then(unwrap<InvoiceDownloadOut>);
}

export function createInvoiceLink(
  slug: string,
  payload: InvoiceLinkCreateIn,
  idToken: string,
): Promise<InvoiceLinkOut> {
  return apiClient
    .POST('/api/customers/{slug}/invoice-links', {
      params: { path: { slug } },
      headers: bearer(idToken),
      body: payload,
    })
    .then(unwrap<InvoiceLinkOut>);
}

export async function deleteInvoiceLink(
  slug: string,
  linkId: string,
  idToken: string,
): Promise<void> {
  await apiClient
    .DELETE('/api/customers/{slug}/invoice-links/{link_id}', {
      params: { path: { slug, link_id: linkId } },
      headers: bearer(idToken),
    })
    .then(unwrap<void>);
}

/** Permanently delete an invoice document — its S3 file and every link
 *  to it. Unlike `deleteInvoiceLink` (unattach), the document does not
 *  return to the pool. */
export async function deleteInvoiceDocument(
  slug: string,
  documentId: string,
  idToken: string,
): Promise<void> {
  await apiClient
    .DELETE('/api/customers/{slug}/invoices/{document_id}', {
      params: { path: { slug, document_id: documentId } },
      headers: bearer(idToken),
    })
    .then(unwrap<void>);
}

export function updateInvoiceLinkFacts(
  slug: string,
  linkId: string,
  payload: InvoiceLinkFactsIn,
  idToken: string,
): Promise<InvoiceLinkOut> {
  return apiClient
    .PUT('/api/customers/{slug}/invoice-links/{link_id}/facts', {
      params: { path: { slug, link_id: linkId } },
      headers: bearer(idToken),
      body: payload,
    })
    .then(unwrap<InvoiceLinkOut>);
}

export function searchTransactions(
  slug: string,
  idToken: string,
  opts: {
    q?: string;
    amount?: string;
    amount_tolerance?: string;
    date_from?: string;
    date_to?: string;
    gl_account?: string;
    fuzzy?: boolean;
    limit?: number;
    offset?: number;
  } = {},
): Promise<TransactionSearchOut> {
  return apiClient
    .GET('/api/customers/{slug}/transaction-search', {
      params: { path: { slug }, query: opts },
      headers: bearer(idToken),
    })
    .then(unwrap<TransactionSearchOut>);
}

export function listTransactionDocuments(
  slug: string,
  transactionIds: string[],
  idToken: string,
  withDownload = false,
): Promise<TransactionDocumentsOut> {
  return apiClient
    .GET('/api/customers/{slug}/transaction-documents', {
      params: {
        path: { slug },
        query: { transaction_ids: transactionIds, with_download: withDownload },
      },
      headers: bearer(idToken),
    })
    .then(unwrap<TransactionDocumentsOut>);
}

/** Mark a transaction "no invoice available" (INP-494). Idempotent. */
export function markNoInvoice(
  slug: string,
  externalTransactionId: string,
  idToken: string,
): Promise<NoInvoiceMarkOut> {
  return apiClient
    .POST('/api/customers/{slug}/no-invoice-marks', {
      params: { path: { slug } },
      headers: bearer(idToken),
      body: { external_transaction_id: externalTransactionId },
    })
    .then(unwrap<NoInvoiceMarkOut>);
}

/** Clear the "no invoice available" mark (undo). */
export async function unmarkNoInvoice(
  slug: string,
  externalTransactionId: string,
  idToken: string,
): Promise<void> {
  await apiClient
    .DELETE('/api/customers/{slug}/no-invoice-marks', {
      params: { path: { slug }, query: { external_transaction_id: externalTransactionId } },
      headers: bearer(idToken),
    })
    .then(unwrap<void>);
}

export function getInvoiceExtraction(
  slug: string,
  documentId: string,
  idToken: string,
): Promise<InvoiceExtractionOut | null> {
  return apiClient
    .GET('/api/customers/{slug}/invoices/{document_id}/extraction', {
      params: { path: { slug, document_id: documentId } },
      headers: bearer(idToken),
    })
    .then(unwrap<{ extraction: InvoiceExtractionOut | null }>)
    .then((envelope) => envelope.extraction);
}

export function saveInvoiceExtraction(
  slug: string,
  documentId: string,
  payload: InvoiceExtractionIn,
  idToken: string,
): Promise<InvoiceExtractionOut> {
  return apiClient
    .PUT('/api/customers/{slug}/invoices/{document_id}/extraction', {
      params: { path: { slug, document_id: documentId } },
      headers: bearer(idToken),
      body: payload,
    })
    .then(unwrap<InvoiceExtractionOut>);
}

/** Re-run the deterministic parser over a document, replacing its
 *  ai-prefilled extraction. Returns the new extraction, or null when the
 *  document has no readable text layer (204). Throws on 409 — a manual
 *  label is never overwritten. */
export function reextractInvoice(
  slug: string,
  documentId: string,
  idToken: string,
): Promise<InvoiceExtractionOut | null> {
  return apiClient
    .POST('/api/customers/{slug}/invoices/{document_id}/reextract', {
      params: { path: { slug, document_id: documentId } },
      headers: bearer(idToken),
    })
    .then((result) => {
      if (result.error) throw new Error('Re-extract failed');
      // 204 (no readable text layer) resolves with an empty body.
      return (result.data as InvoiceExtractionOut | undefined) ?? null;
    });
}

export function getMatchSuggestions(
  slug: string,
  documentId: string,
  idToken: string,
): Promise<MatchSuggestionsOut> {
  return apiClient
    .GET('/api/customers/{slug}/invoices/{document_id}/match-suggestions', {
      params: { path: { slug, document_id: documentId } },
      headers: bearer(idToken),
    })
    .then(unwrap<MatchSuggestionsOut>);
}

export function autoMatchInvoices(slug: string, idToken: string): Promise<AutoMatchOut> {
  return apiClient
    .POST('/api/customers/{slug}/invoices/auto-match', {
      params: { path: { slug } },
      headers: bearer(idToken),
    })
    .then(unwrap<AutoMatchOut>);
}

/** Links the batch auto-matcher wrote, newest first — the spot-check
 *  audit list. */
export function listAutoMatchedLinks(
  slug: string,
  idToken: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<AutoMatchedLinksOut> {
  return apiClient
    .GET('/api/customers/{slug}/auto-matched-links', {
      params: { path: { slug }, query: opts },
      headers: bearer(idToken),
    })
    .then(unwrap<AutoMatchedLinksOut>);
}
